import React, { useState, useEffect } from 'react';
import { Box, TextField, Alert, Typography, Button } from '@mui/material';
import { ContentCopy, Key, Visibility } from '@mui/icons-material';
import { useGetGitHubConnectionPublicKey } from '../hooks/useGitHubConnection';

interface SSHKeyConfigurationProps {
    existingPublicKey?: string;
    keyType?: 'rsa' | 'ed25519';
    connectionId?: string;
}

const SSHKeyConfiguration: React.FC<SSHKeyConfigurationProps> = ({
    existingPublicKey,
    keyType = 'ed25519',
    connectionId
}) => {
    const [publicKey, setPublicKey] = useState(existingPublicKey || '');
    const [copySuccess, setCopySuccess] = useState(false);
    const getPublicKey = useGetGitHubConnectionPublicKey(connectionId || '');

    useEffect(() => {
        if (existingPublicKey) {
            setPublicKey(existingPublicKey);
        }
    }, [existingPublicKey]);

    const handleShowPublicKey = async () => {
        if (!connectionId) return;
        
        try {
            const result = await getPublicKey.mutateAsync();
            if (result.success && result.publicKey) {
                setPublicKey(result.publicKey);
            }
        } catch (error) {
            console.error('Failed to fetch public key:', error);
        }
    };



    const copyToClipboard = async () => {
        try {
            await navigator.clipboard.writeText(publicKey);
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000);
        } catch (error) {
            console.error('Failed to copy to clipboard:', error);
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 2, border: '1px solid #ddd', borderRadius: 1 }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Key /> SSH Key Configuration
            </Typography>

            <Alert severity="info">
                An {keyType.toUpperCase()} SSH key pair will be automatically generated when you save this connection. The private key is securely stored and never displayed.
            </Alert>

            {connectionId && !publicKey && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Button
                        variant="outlined"
                        startIcon={<Visibility />}
                        onClick={handleShowPublicKey}
                        disabled={getPublicKey.isPending}
                    >
                        {getPublicKey.isPending ? 'Loading...' : 'Show Public Key'}
                    </Button>
                </Box>
            )}

            {publicKey && (
                <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Public Key:</Typography>
                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        value={publicKey}
                        InputProps={{
                            readOnly: true,
                            style: { fontFamily: 'monospace', fontSize: '12px' },
                            endAdornment: (
                                <ContentCopy 
                                    sx={{ cursor: 'pointer', color: copySuccess ? 'green' : 'inherit' }}
                                    onClick={copyToClipboard}
                                />
                            )
                        }}
                        sx={{ mb: 1 }}
                    />
                    
                    <Alert severity="warning">
                        <Typography variant="body2">
                            <strong>Next Steps:</strong>
                            <br />
                            1. Copy the public key above
                            <br />
                            2. Add it to your GitHub repository as a Deploy Key (Settings → Deploy keys)
                            <br />
                            3. Or add it to your GitHub account (Settings → SSH and GPG keys)
                        </Typography>
                    </Alert>
                </Box>
            )}
        </Box>
    );
};

export default SSHKeyConfiguration;